// @ts-check
/** @jsx jsx */
import { gql } from '@apollo/client';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import {
  ToggleFavorite,
  useFavorite,
} from '@bottlebooks/gatsby-plugin-bookmarks/src';
import useLink from '../../useLink';
import {
  hasDifferentExhibitor,
  ProductHeaderDetails,
  ProductHeaderDetailsFragment,
  ProductName,
  ProductNameFragment,
  ProductProducer,
  ProductProducerNameFragment,
  ProductStand,
  ProductStandFragment,
} from '../Product/ProductHeader';
import ProductLabel from '../Product/ProductLabel';
import ProductImageCol, {
  ProductImageFragment_large,
} from '../ProductPage/ProductImageCol';
import ProductNavigation from '../ProductPage/ProductNavigation';
import {
  ProductMainCol,
  ProductPageHeaderRow,
} from '../ProductPage/ProductPageHeader';

export default function EventProductPageHeader({
  product,
  next,
  previous,
  standPath,
  overlapImage,
}) {
  const link = useLink();
  const favorite = useFavorite(
    product && { type: 'product', id: product.productId }
  );
  const hasOtherExhibitor = hasDifferentExhibitor(product);
  return (
    <ProductPageHeaderRow sx={{ justifyContent: 'center' }}>
      <ProductImageCol product={product} overlap={overlapImage} />
      <ProductMainCol>
        <ProductLabel product={product} sx={{ marginBottom: 3.5 }} />
        <ProductStand
          stand={product?.registration?.stand || product?.stand}
          exhibitor={hasOtherExhibitor && product?.exhibitor}
          to={hasOtherExhibitor && standPath}
        />
        <ProductProducer
          producer={product?.producer}
          to={link.producer(product.producer)}
        />
        <ProductName as="h1" product={product} />
        <ProductHeaderDetails product={product} showMorePricingInfo={true} />
        <ToggleFavorite
          sx={{ marginTop: 3, marginLeft: -2, padding: 0, paddingRight: 3 }}
          isToggled={favorite.isSet}
          onClick={favorite.toggle}
        />
      </ProductMainCol>

      <ProductNavigation previous={previous} next={next} />
    </ProductPageHeaderRow>
  );
}

export const fragment = graphql`
  fragment bb_EventProductPageHeaderRegisteredProduct on Bottlebooks_RegisteredProduct {
    ...bb_ProductLabel_RegisteredProduct
    ...bb_ProductHeaderDetails_RegisteredProduct
    product {
      ...bb_EventProductPageHeader
    }
  }

  fragment bb_EventProductPageHeader on Bottlebooks_Product {
    productId
    ...bb_ProductHeaderDetails
    producer {
      ...useLink_bb_Producer
    }
  }

  fragment EventProductPageHeader on Product {
    ...ProductImageLarge
    ...ProductStand
    ...ProductProducerName
    ...ProductName
    ...ProductHeaderDetails
  }
`;

export const EventProductPageHeaderFragment = gql`
  fragment EventProductPageHeader on Product {
    productId
    ...ProductImageLarge
    ...ProductStand
    ...ProductProducerName
    ...ProductName
    ...ProductHeaderDetails
  }

  ${ProductImageFragment_large}
  ${ProductStandFragment}
  ${ProductProducerNameFragment}
  ${ProductNameFragment}
  ${ProductHeaderDetailsFragment}
`;
